import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { vars } from 'src/core/themes';
import { useCampaignsTableMetricsData } from 'src/tables/SponsoredListings/CampaignsTableV2/useCampaignsTableMetricsData';
import { StandardDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  DateTimeRange,
  FlightDuration,
  SponsoredListingsCampaign,
} from 'src/WebApiController';

import * as styles from './PerformanceChart.css';

type PerformanceChartDataPoint = {
  date: string;
  dateKey: string;
  revenue: number;
  spend: number;
  roas: number | null;
};

type ComputeChartResult = {
  data: PerformanceChartDataPoint[];
  hasUnsupportedFlights: boolean;
};

// Colors used for the chart elements
const REVENUE_COLOR = vars.color.textBrand;
const SPEND_COLOR = vars.color.textSecondary;
const ROAS_COLOR = vars.color.surfaceInverted;

export function computeChartData(
  campaigns: SponsoredListingsCampaign[] | null | undefined,
  dateRangeFilter?: DateTimeRange | null
): ComputeChartResult {
  if (!campaigns || campaigns.length === 0) {
    return { data: [], hasUnsupportedFlights: false };
  }

  // Filter out campaigns with unsupported flight durations (Weekly, Monthly)
  const supportedCampaigns = campaigns.filter(
    (c) =>
      c.flightDuration !== FlightDuration.Weekly &&
      c.flightDuration !== FlightDuration.Monthly
  );
  const hasUnsupportedFlights = supportedCampaigns.length < campaigns.length;

  // Aggregate flight data by date period (using start date of the flight as key)
  const byDate = new Map<
    string,
    { revenue: number; spend: number; dateTs: number }
  >();

  for (const campaign of supportedCampaigns) {
    const allFlights =
      campaign.flights ??
      (campaign.activeFlight ? [campaign.activeFlight] : []);

    for (const flight of allFlights) {
      if (!flight.dateRange?.start) continue;

      const startDate = new Date(flight.dateRange.start);

      // Filter flights outside the selected date range
      if (dateRangeFilter) {
        if (dateRangeFilter.start) {
          const rangeStart = new Date(dateRangeFilter.start);
          if (startDate < rangeStart) continue;
        }
        if (dateRangeFilter.end) {
          const rangeEnd = new Date(dateRangeFilter.end);
          if (startDate > rangeEnd) continue;
        }
      }

      // Format as short date key: YYYY-MM-DD
      const dateKey = startDate.toISOString().slice(0, 10);
      const dateTs = startDate.getTime();

      const existing = byDate.get(dateKey) ?? {
        revenue: 0,
        spend: 0,
        dateTs,
      };
      existing.revenue += flight.salesProceeds ?? 0;
      existing.spend += flight.flightSpend ?? 0;
      byDate.set(dateKey, existing);
    }
  }

  if (byDate.size === 0) {
    return { data: [], hasUnsupportedFlights };
  }

  // Sort by date ascending and compute ROAS per point
  const sorted = Array.from(byDate.entries()).sort(
    ([, a], [, b]) => a.dateTs - b.dateTs
  );

  const data = sorted.map(([dateKey, { revenue, spend }]) => {
    // Format date as MM/DD (zero-padded)
    const date = new Date(dateKey + 'T00:00:00Z');
    const label = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    });

    return {
      date: label,
      dateKey,
      revenue,
      spend: -spend,
      roas: spend > 0 ? revenue / spend : null,
    };
  });

  return { data, hasUnsupportedFlights };
}

type PerformanceTooltipProps = {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number | null;
    color: string;
  }>;
  label?: string;
};

function PerformanceTooltip({
  active,
  payload,
  label,
}: PerformanceTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const revenueEntry = payload.find((p) => p.name === 'revenue');
  const spendEntry = payload.find((p) => p.name === 'spend');
  const roasEntry = payload.find((p) => p.name === 'roas');

  return (
    <div className={styles.tooltip}>
      {label && <div className={styles.tooltipDate}>{label}</div>}
      {revenueEntry && (
        <div className={styles.tooltipRow}>
          <div
            className={styles.tooltipSwatch}
            style={{ backgroundColor: REVENUE_COLOR }}
          />
          <span className={styles.tooltipLabel}>Revenue</span>
          <span className={styles.tooltipValue}>
            {formatCurrency(revenueEntry.value ?? 0, 'USD', 0)}
          </span>
        </div>
      )}
      {spendEntry && (
        <div className={styles.tooltipRow}>
          <div
            className={styles.tooltipSwatch}
            style={{ backgroundColor: SPEND_COLOR }}
          />
          <span className={styles.tooltipLabel}>Spend</span>
          <span className={styles.tooltipValue}>
            {formatCurrency(Math.abs(spendEntry.value ?? 0), 'USD', 0)}
          </span>
        </div>
      )}
      {roasEntry && roasEntry.value != null && (
        <div className={styles.tooltipRow}>
          <div
            className={styles.tooltipSwatch}
            style={{ backgroundColor: ROAS_COLOR }}
          />
          <span className={styles.tooltipLabel}>ROAS</span>
          <span className={styles.tooltipValue}>
            {roasEntry.value.toFixed(2)}x
          </span>
        </div>
      )}
    </div>
  );
}

type PerformanceChartProps = {
  allCampaigns: SponsoredListingsCampaign[] | null;
  dateRangeFilter: DateTimeRange | null;
  onDateRangeFilterChange: (value: DateTimeRange | null) => void;
};

export function PerformanceChart({
  allCampaigns,
  dateRangeFilter,
  onDateRangeFilterChange,
}: PerformanceChartProps) {
  const { campaigns: campaignsWithMetrics } = useCampaignsTableMetricsData(
    allCampaigns,
    dateRangeFilter
  );

  const { data: chartData, hasUnsupportedFlights } = useMemo(
    () => computeChartData(campaignsWithMetrics, dateRangeFilter),
    [campaignsWithMetrics, dateRangeFilter]
  );

  const currencyTickFormatter = (value: number) =>
    formatCurrency(Math.abs(value), 'USD', 0);

  const roasTickFormatter = (value: number) => `${value.toFixed(1)}x`;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.title}>Performance</div>
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{ backgroundColor: REVENUE_COLOR }}
              />
              <span>Revenue</span>
            </div>
            <div className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{ backgroundColor: SPEND_COLOR }}
              />
              <span>Spend</span>
            </div>
            <div className={styles.legendItem}>
              <span
                className={styles.legendDash}
                style={{ backgroundColor: ROAS_COLOR }}
              />
              <span>ROAS</span>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <DateRangeSelector
            compactTrigger
            presetNames={StandardDateRangePresetNames}
            value={dateRangeFilter}
            onChange={({ value }) => onDateRangeFilterChange(value ?? null)}
          />
        </div>
      </div>
      {chartData.length === 0 ? (
        <div className={styles.emptyState}>No performance data available</div>
      ) : (
        <div className={styles.chartWrapper}>
          {hasUnsupportedFlights && (
            <div className={styles.unsupportedFlightsWarning}>
              Monthly and weekly flights are not supported
            </div>
          )}
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 40, left: 8, bottom: 8 }}
              barGap={-12}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={vars.color.borderPrimary}
                vertical={false}
              />
              <ReferenceLine
                yAxisId="left"
                y={0}
                stroke={vars.color.borderPrimary}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: vars.color.textSecondary }}
                axisLine={false}
                tickLine={false}
                interval={Math.max(0, Math.ceil(chartData.length / 8) - 1)}
              />
              <YAxis
                yAxisId="left"
                tickFormatter={currencyTickFormatter}
                tick={{ fontSize: 11, fill: vars.color.textSecondary }}
                axisLine={false}
                tickLine={false}
                width={70}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={roasTickFormatter}
                tick={{ fontSize: 11, fill: ROAS_COLOR }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip
                isAnimationActive={false}
                content={(props) => (
                  <PerformanceTooltip
                    active={props.active}
                    payload={
                      props.payload as PerformanceTooltipProps['payload']
                    }
                    label={props.label as string}
                  />
                )}
              />
              <Bar
                yAxisId="left"
                dataKey="revenue"
                fill={REVENUE_COLOR}
                name="revenue"
                radius={[8, 8, 0, 0]}
                barSize={12}
              />
              <Bar
                yAxisId="left"
                dataKey="spend"
                fill={SPEND_COLOR}
                name="spend"
                radius={[8, 8, 0, 0]}
                barSize={12}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roas"
                stroke={ROAS_COLOR}
                name="roas"
                dot={false}
                activeDot={{ r: 5 }}
                strokeWidth={2}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
